/**
 * Content Engagement Components
 *
 * Provides interactive functionality for content engagement features:
 * - Scroll progress bar: Visual indicator of reading progress
 * - Table of Contents toggle: Collapsible ToC navigation
 *
 * Cross-pollinated from PixemWeb theme.
 *
 * @since 1.22.0
 */

/**
 * Configuration constants
 */
const CONFIG = {
	// Selectors
	SCROLL_PROGRESS_SELECTOR: '.devwp-scroll-progress',
	TOC_SELECTOR: '.devwp-toc',
	TOC_TOGGLE_SELECTOR: '.devwp-toc__toggle',
	TOC_LIST_SELECTOR: '.devwp-toc__list',
	TOC_COLLAPSED_CLASS: 'devwp-toc--collapsed',

	// Throttle delay for scroll events (ms)
	SCROLL_THROTTLE: 16, // ~60fps
};

/**
 * Scroll Progress Bar Module
 *
 * Updates a CSS custom property (--scroll-progress) based on
 * how far the user has scrolled through the document.
 */
const scrollProgress = {
	element: null,
	ticking: false,

	/**
	 * Initialize scroll progress tracking
	 */
	init() {
		this.element = document.querySelector(
			CONFIG.SCROLL_PROGRESS_SELECTOR
		);

		if ( ! this.element ) {
			return;
		}

		// Bind scroll handler with throttling
		window.addEventListener( 'scroll', () => this.onScroll(), {
			passive: true,
		} );

		// Set initial value
		this.updateProgress();
	},

	/**
	 * Handle scroll event with requestAnimationFrame throttling
	 */
	onScroll() {
		if ( ! this.ticking ) {
			window.requestAnimationFrame( () => {
				this.updateProgress();
				this.ticking = false;
			} );
			this.ticking = true;
		}
	},

	/**
	 * Calculate and update the scroll progress
	 */
	updateProgress() {
		if ( ! this.element ) {
			return;
		}

		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const docHeight = document.documentElement.scrollHeight;
		const winHeight = window.innerHeight;
		const scrollPercent = ( scrollTop / ( docHeight - winHeight ) ) * 100;

		// Clamp between 0 and 100
		const progress = Math.min( 100, Math.max( 0, scrollPercent ) );

		this.element.style.setProperty( '--scroll-progress', progress );
	},
};

/**
 * Table of Contents Toggle Module
 *
 * Handles the expand/collapse functionality for the ToC.
 */
const tocToggle = {
	tocElements: null,

	/**
	 * Initialize ToC toggle functionality
	 */
	init() {
		this.tocElements = document.querySelectorAll( CONFIG.TOC_SELECTOR );

		if ( ! this.tocElements.length ) {
			return;
		}

		this.tocElements.forEach( ( toc ) => this.initToc( toc ) );
	},

	/**
	 * Initialize a single ToC element
	 *
	 * @param {HTMLElement} toc The ToC container element
	 */
	initToc( toc ) {
		const toggle = toc.querySelector( CONFIG.TOC_TOGGLE_SELECTOR );
		const list = toc.querySelector( CONFIG.TOC_LIST_SELECTOR );

		if ( ! toggle || ! list ) {
			return;
		}

		// Handle click events
		toggle.addEventListener( 'click', () => {
			this.toggleToc( toc, toggle, list );
		} );

		// Handle keyboard events for accessibility
		toggle.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Enter' || e.key === ' ' ) {
				e.preventDefault();
				this.toggleToc( toc, toggle, list );
			}
		} );
	},

	/**
	 * Toggle the ToC expanded/collapsed state
	 *
	 * @param {HTMLElement} toc    The ToC container
	 * @param {HTMLElement} toggle The toggle button
	 * @param {HTMLElement} list   The ToC list
	 */
	toggleToc( toc, toggle, list ) {
		const isExpanded = toggle.getAttribute( 'aria-expanded' ) === 'true';

		// Update ARIA state
		toggle.setAttribute( 'aria-expanded', String( ! isExpanded ) );

		// Toggle visibility class
		toc.classList.toggle( CONFIG.TOC_COLLAPSED_CLASS, isExpanded );

		// Toggle list visibility
		list.hidden = isExpanded;
	},
};

/**
 * ToC Active Heading Tracking Module
 *
 * Highlights the currently visible section in the ToC.
 */
const tocActiveTracking = {
	headings: null,
	tocLinks: null,
	observer: null,

	/**
	 * Initialize active heading tracking
	 */
	init() {
		const tocs = document.querySelectorAll( CONFIG.TOC_SELECTOR );
		if ( ! tocs.length ) {
			return;
		}

		this.tocLinks = document.querySelectorAll(
			CONFIG.TOC_SELECTOR + ' .devwp-toc__link'
		);
		if ( ! this.tocLinks.length ) {
			return;
		}

		// Get unique heading IDs from all ToC links
		const headingIds = [
			...new Set(
				Array.from( this.tocLinks )
					.map( ( link ) => {
						const href = link.getAttribute( 'href' );
						return href ? href.slice( 1 ) : null;
					} )
					.filter( Boolean )
			),
		];

		// Get corresponding heading elements
		this.headings = headingIds
			.map( ( id ) => document.getElementById( id ) )
			.filter( Boolean );

		if ( ! this.headings.length ) {
			return;
		}

		// Set up Intersection Observer
		this.setupObserver();
	},

	/**
	 * Set up the Intersection Observer for headings
	 */
	setupObserver() {
		const options = {
			rootMargin: '-80px 0px -80% 0px',
			threshold: 0,
		};

		this.observer = new IntersectionObserver( ( entries ) => {
			entries.forEach( ( entry ) => {
				if ( entry.isIntersecting ) {
					this.setActiveLink( entry.target.id );
				}
			} );
		}, options );

		this.headings.forEach( ( heading ) => {
			this.observer.observe( heading );
		} );
	},

	/**
	 * Set the active ToC link
	 *
	 * @param {string} id The ID of the active heading
	 */
	setActiveLink( id ) {
		this.tocLinks.forEach( ( link ) => {
			link.parentElement.classList.remove( 'devwp-toc__item--active' );
		} );

		// Add active class to all matching links across ToC instances
		const activeLinks = document.querySelectorAll(
			`.devwp-toc__link[href="#${ CSS.escape( id ) }"]`
		);
		activeLinks.forEach( ( link ) => {
			link.parentElement.classList.add( 'devwp-toc__item--active' );
		} );
	},
};

/**
 * Content Engagement Module
 *
 * Main module that initializes all content engagement features.
 */
const contentEngagement = {
	/**
	 * Initialize all content engagement features
	 */
	init() {
		scrollProgress.init();
		tocToggle.init();
		tocActiveTracking.init();
	},
};

document.addEventListener( 'DOMContentLoaded', () => {
	contentEngagement.init();
} );

// Export for testing and external access
export {
	scrollProgress,
	tocToggle,
	tocActiveTracking,
	contentEngagement,
	CONFIG,
};
export default contentEngagement;
